'use client';

import { motion, MotionProps } from 'motion/react';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { twJoin } from 'tailwind-merge';

import { NetworksGrid } from '@/components/Network/NetworksGrid';
import { Pagination } from '@/components/Pagination';
import { StoresGrid } from '@/components/Store/StoresGrid';
import { usePageTextContent } from '@/context/pageTextContent';
import { useResponsiveness } from '@/hooks/useResponsiveness';
import { clsx } from '@/utils/helpers';

import { useSearchPageStore } from '../../useSearchPageStore';

import { SearchResult } from './SearchResult';

export type SearchProps = {
  networks: AdaptedNetwork[];
  stores: Paginated<AdaptedStore>;
};

export function Search({ networks, stores }: SearchProps) {
  const { content } = usePageTextContent<SearchPageContent>();

  const { networksGiftcard, storesGiftcard } = content;

  const { isMapOpen, isMapExpanded } = useSearchPageStore();

  const { isMobile } = useResponsiveness();

  const searchParams = useSearchParams();

  const currentPage = searchParams?.get('page');
  const isFirstPage = !currentPage || currentPage === '1';

  const hasNetworks = networks.length > 0;

  const containerAnimation = useMemo<MotionProps['animate']>(() => {
    if (isMobile)
      return { maxWidth: '100%', marginTop: isMapExpanded ? '80vh' : '50vh' };

    return { maxWidth: isMapOpen ? '43.75rem' : '90rem' };
  }, [isMapOpen, isMapExpanded, isMobile]);

  return (
    <motion.div
      animate={containerAnimation}
      className={twJoin(
        'flex w-full flex-col items-center space-y-[2.125rem] rounded-t-lg px-5 py-8',
        'md:items-center md:rounded-t-lg md:px-5 lg:items-start lg:rounded-t-none lg:px-10 lg:py-10',
        'z-[2] bg-gray-50',
      )}
    >
      <div className="flex w-full justify-center py-2 lg:hidden">
        <hr className="h-0.5 w-8 rounded-full bg-blue-800" />
      </div>

      {hasNetworks && isFirstPage && (
        <SearchResult title={networksGiftcard}>
          <NetworksGrid
            networks={networks}
            className={clsx(
              `lg:${isMapOpen ? '1.5rem' : '1rem'}`,
              `lg:${isMapOpen ? 'grid-cols-2' : 'grid-cols-4'}`,
            )}
          />
        </SearchResult>
      )}

      <SearchResult title={storesGiftcard}>
        <StoresGrid stores={stores.items} isCardHorizontal={isMapOpen} />
      </SearchResult>

      <div className="flex w-full justify-center">
        <Pagination pagination={stores} />
      </div>
    </motion.div>
  );
}
